(ns hai.library.connections.card
  "Connection card. Mirrors the mini-apps card structure (head / status /
   foot) but with no toggle — connections are enabled/disabled elsewhere.
   Shared scaffolding lives in [[hai.web-app.entity-card]]."
  (:require
   [clojure.string :as str]
   [hai.library.connections.common :as common]
   [hai.library.connections.invalid-reason :as invalid-reason]
   [hai.ui.components.card :as ui-card]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.tokens :as t]
   [hai.web-app.entity-card :as entity-card]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Container
;; ---------------------------------------------------------------------------

(o/defstyled connection-card :article
  {:display "grid"
   :grid-auto-rows "min-content"
   :gap "14px"
   :padding "18px"
   :min-height "168px"
   :border-radius "14px"
   :background t/--card-bg
   :border "1px solid var(--card-forest-70)"
   :box-shadow "0 1px 0 var(--card-ink-04), 0 1px 2px var(--card-ink-04)"
   :transition "background .15s ease, border-color .15s ease"
   :position "relative"}
  ;; Bulk-action checkbox lives in `ui-card/edit-overlay` (absolute top-right).
  ;; Default opacity 0 expects the `card-hover` mixin; we don't use it, so
  ;; force the overlay visible.
  [ui-card/edit-overlay {:opacity "1"}])

;; ---------------------------------------------------------------------------
;; Head row — icon + title stack
;; ---------------------------------------------------------------------------

(o/defstyled head-row :div
  {:display "flex" :align-items "center" :gap "12px"})

(o/defstyled title-stack :div
  {:flex "1" :min-width "0"
   :display "flex" :flex-direction "column" :gap "2px"})

(o/defstyled type-label :h3
  {:margin "0"
   :font-size "15px" :font-weight "600" :line-height "1.25"
   :letter-spacing "-0.005em"
   :color t/--card-ink
   :white-space "nowrap" :overflow "hidden" :text-overflow "ellipsis"})

(o/defstyled description :span
  {:font-size "13px" :line-height "1.4"
   :color t/--card-text-muted
   :display "-webkit-box"
   :-webkit-line-clamp "2"
   :-webkit-box-orient "vertical"
   :overflow "hidden"}
  [:&.empty {:color t/--card-text-faint :font-style "italic"}])

;; ---------------------------------------------------------------------------
;; Status row — dot + label + optional token ID
;; ---------------------------------------------------------------------------

(o/defstyled status-row :div
  {:display "flex" :align-items "center" :gap "8px"
   :position "relative"})

(o/defstyled status-dot :span
  {:width "6px" :height "6px"
   :flex-shrink "0"
   :border-radius "50%"
   :background t/--card-track}
  [:&.connected {:background t/--card-forest :box-shadow "0 0 0 3px var(--card-forest-10)"}]
  [:&.error     {:background t/--card-error  :box-shadow "0 0 0 3px var(--card-error-12)"}])

(o/defstyled status-label :span
  {:font-size "12px" :font-weight "500"
   :color t/--card-text-muted}
  [:&.connected {:color t/--card-forest}])

(o/defstyled token-row :div
  {:display "flex" :align-items "baseline" :gap "6px"
   :font-size "12px"
   :color t/--card-text-muted
   :overflow-wrap "anywhere"
   :word-break "break-all"})

(o/defstyled token-label :span
  {:font-weight "500" :flex "0 0 auto"})

(o/defstyled token-value :span
  {:font-family "ui-monospace, 'JetBrains Mono', Menlo, monospace"
   :letter-spacing "0.01em"
   :color t/--card-ink
   :min-width "0"})

;; ---------------------------------------------------------------------------
;; Foot row — sharing only (no toggle on connection cards)
;; ---------------------------------------------------------------------------

(o/defstyled foot-row :div
  {:display "flex" :flex-direction "column" :align-items "stretch"
   :gap "4px"
   ;; Extra breathing room above so the sharing slot isn't crowded by
   ;; the status row; the card's grid gap sets the baseline rhythm.
   :margin-top "10px"})

;; ---------------------------------------------------------------------------
;; Helpers
;; ---------------------------------------------------------------------------

(defn- status-for
  [{:connection/keys [api-key-invalid?]}]
  (if api-key-invalid? :error :connected))

(defn- invalid-description
  "What's wrong with an invalid connection (see [[invalid-reason/describe]]),
  nil for a healthy one."
  [{:connection/keys [api-key-invalid? api-key-invalid-reason]}]
  (when api-key-invalid?
    (invalid-reason/describe api-key-invalid-reason)))

;; ---------------------------------------------------------------------------
;; Top-level render
;; ---------------------------------------------------------------------------

(defn render
  [collection row]
  (let [{:connection/keys [type token-id]
         :as item}        (:form/current-value row)
        description-text  (:connection/description item)
        icon              (get common/type->icon type)
        type-text         (some-> type name str/capitalize)
        described?        (seq description-text)
        status            (status-for item)
        problem           (invalid-description item)]
    [connection-card
     [cards/card-select-checkbox collection row]
     [head-row
      [entity-card/icon-frame (when icon [icon])]
      [title-stack
       [type-label (or type-text "Connection")]
       (if described?
         [description description-text]
         [description {:class "empty"} "No description"])]]
     [status-row
      [status-dot {:class (name status)}]
      [status-label {:class (name status) :title (:explanation problem)}
       (or (:status problem) "Connected")]]
     (when token-id
       [token-row
        [token-label "Token ID:"]
        [token-value token-id]])
     [foot-row
      [entity-card/sharing-block
       {:collection         collection
        :entity             item
        :aria-label         "Share connection"
        :dialog-description "People with access to this connection"}]]]))
